import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { track } from "@/lib/analytics";
import { PageHeader } from "@/components/PageHeader";
import { TextField, SelectField, FieldHelp, primaryBtnCls, outlineBtnCls } from "@/components/FormFields";
import { JURISDICTIONS, ENVIRONMENTS, WCAG_TARGETS, jurisdictionLabel } from "@/lib/taxonomy";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

const emptyForm = {
  name: "",
  contact_email: "",
  jurisdiction: "eu",
  site_name: "",
  site_base_url: "",
  site_environment: "production",
  site_wcag_target: "wcag22aa",
};

export default function Clients() {
  const [clients, setClients] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [withSite, setWithSite] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);
  // Holds the client awaiting deletion, which is also what opens the modal.
  // Keeping the row rather than the id lets the dialog name what is at stake.
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = () => api.get("/clients").then((r) => setClients(r.data)).catch(() => toast.error("Could not load clients"));
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const reset = () => { setForm(emptyForm); setWithSite(false); setShowForm(false); };

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      // One request for both records: the API creates them in a single
      // transaction, so a site rejected by the plan limit does not leave a
      // half-created client behind.
      const payload = {
        name: form.name,
        contact_email: form.contact_email || null,
        jurisdiction: form.jurisdiction || null,
        first_site: withSite
          ? {
              name: form.site_name,
              base_url: form.site_base_url,
              wcag_target: form.site_wcag_target,
              environment: form.site_environment,
            }
          : null,
      };
      const { data } = await api.post("/clients", payload);
      track("client_created", { with_first_site: withSite, jurisdiction: form.jurisdiction || "unset" });
      if (data.site) track("site_created", { wcag_target: form.site_wcag_target, environment: form.site_environment });
      toast.success(data.site ? "Client and first site created" : "Client created");
      reset();
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create client");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      await api.delete(`/clients/${confirmDelete.id}`);
      toast.success("Client deleted");
      setConfirmDelete(null);
      load();
    } catch (err) {
      // The cascade runs server side, so a failure here leaves the client
      // intact. Say so rather than closing on an action that did not happen.
      toast.error(err.response?.data?.detail || "Could not delete client");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div data-testid="clients-page">
      <PageHeader
        title="Clients"
        description="Every audit belongs to a client's site."
        actions={
          <button data-testid="new-client-btn" onClick={() => setShowForm(!showForm)} className={primaryBtnCls}>
            New client
          </button>
        }
      />

      {showForm && (
        <form onSubmit={create} className="bg-white border border-gray-200 rounded-sm p-5 mb-6 max-w-lg">
          <TextField
            id="client-name" testId="client-name-input" label="Client name" required
            value={form.name} onChange={set("name")}
          />
          <TextField
            id="client-email" testId="client-email-input" label="Contact email" hint="(optional)"
            type="email" value={form.contact_email} onChange={set("contact_email")}
          />
          <SelectField
            id="client-jurisdiction" testId="client-jurisdiction-select" label="Jurisdiction"
            options={JURISDICTIONS} placeholder="Not set" describedBy="client-jurisdiction-help"
            value={form.jurisdiction} onChange={set("jurisdiction")} className="mb-1.5"
          />
          <FieldHelp id="client-jurisdiction-help">
            Every site for this client inherits it, and accessibility statements start from
            the matching template. You can pick a different one per document.
          </FieldHelp>

          <div className="border-t border-gray-200 pt-4 mt-4">
            <label className="flex items-start gap-2 mb-3">
              <input
                type="checkbox" data-testid="client-with-site-toggle" checked={withSite}
                onChange={(e) => setWithSite(e.target.checked)}
                className="mt-1 accent-[#16324F] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#16324F]"
              />
              <span className="text-sm font-medium">Add this client's first site now</span>
            </label>

            {withSite && (
              <div data-testid="client-first-site-fields">
                <TextField
                  id="client-site-name" testId="client-site-name-input" label="Site name" required
                  value={form.site_name} onChange={set("site_name")}
                />
                <TextField
                  id="client-site-url" testId="client-site-url-input" label="Base URL" required mono
                  placeholder="https://client-site.com"
                  value={form.site_base_url} onChange={set("site_base_url")}
                />
                <SelectField
                  id="client-site-environment" testId="client-site-environment-select" label="Environment"
                  options={ENVIRONMENTS} value={form.site_environment} onChange={set("site_environment")}
                />
                <SelectField
                  id="client-site-target" testId="client-site-target-select" label="WCAG target"
                  options={WCAG_TARGETS} value={form.site_wcag_target} onChange={set("site_wcag_target")}
                  className="mb-4"
                />
              </div>
            )}
          </div>

          <button data-testid="client-create-submit" type="submit" disabled={busy} className={primaryBtnCls}>
            {busy ? "Creating..." : withSite ? "Create client and site" : "Create client"}
          </button>
        </form>
      )}

      <div className="table-card" tabIndex={0} role="region" aria-label="List of clients">
        <table className="w-full min-w-[780px] text-left border-collapse text-sm" data-testid="clients-table">
          <caption className="sr-only">List of clients</caption>
          <thead>
            <tr className="border-b border-gray-300">
              <th scope="col" className="px-5 py-3 font-heading font-semibold">Name</th>
              <th scope="col" className="px-5 py-3 font-heading font-semibold">Jurisdiction</th>
              <th scope="col" className="px-5 py-3 font-heading font-semibold">Contact</th>
              <th scope="col" className="px-5 py-3 font-heading font-semibold text-right">Sites</th>
              <th scope="col" className="px-5 py-3 font-heading font-semibold text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {clients.length === 0 && (
              <tr>
                <td colSpan={5} className="px-5 py-10 text-center text-ink-muted" data-testid="clients-empty">
                  No clients yet. Create your first client.
                </td>
              </tr>
            )}
            {clients.map((c) => (
              <tr key={c.id} className="border-b border-gray-200 last:border-0" data-testid={`client-row-${c.id}`}>
                <td className="px-5 py-3 font-medium">{c.name}</td>
                <td className="px-5 py-3 text-ink-muted" data-testid={`client-jurisdiction-${c.id}`}>
                  {c.jurisdiction ? jurisdictionLabel(c.jurisdiction) : <span className="text-ink-subtle">Not set</span>}
                </td>
                <td className="px-5 py-3 font-mono text-xs text-ink-muted max-w-[260px] truncate" title={c.contact_email || undefined}>{c.contact_email || "-"}</td>
                <td className="px-5 py-3 font-mono text-right">{c.site_count}</td>
                <td className="px-5 py-3 text-right whitespace-nowrap">
                  <Link
                    to={`/sites?client=${c.id}`}
                    data-testid={`client-view-sites-${c.id}`}
                    className="text-xs text-[#16324F] hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[#16324F] rounded-sm px-1 mr-2"
                  >
                    View sites<span className="sr-only"> for {c.name}</span>
                  </Link>
                  <button
                    data-testid={`client-delete-${c.id}`} onClick={() => setConfirmDelete(c)}
                    className="text-xs text-[#C62828] hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[#16324F] rounded-sm px-1"
                  >
                    Delete<span className="sr-only"> {c.name}</span>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Same Dialog the billing and settings confirmations use. A native
          confirm() sat here before, which cannot be styled, cannot name the
          record, and renders as a browser chrome alert on a page that is
          otherwise the product. */}
      <Dialog open={!!confirmDelete} onOpenChange={(open) => { if (!open && !deleting) setConfirmDelete(null); }}>
        <DialogContent data-testid="client-delete-modal">
          <DialogHeader>
            <DialogTitle>Delete {confirmDelete?.name}?</DialogTitle>
            <DialogDescription>
              This also deletes every site for this client, along with their audits,
              tracked issues, checklist results, and generated reports. It cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <button data-testid="client-delete-cancel-btn" onClick={() => setConfirmDelete(null)} disabled={deleting}
              className={outlineBtnCls}>
              Cancel
            </button>
            <button data-testid="client-delete-confirm-btn" onClick={remove} disabled={deleting}
              className="bg-[#C62828] text-white rounded-sm px-4 py-2 text-sm hover:bg-[#A61F1F] transition-colors disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C62828] focus-visible:ring-offset-2">
              {deleting ? "Deleting..." : "Delete client"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
